// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { API_URL } from 'Config';
import { Dispatch } from 'redux';
import { AppDispatch, RootState } from 'Store';
import { deleteRequest, getJson, patchJson, postJson } from 'Utils/http';
import { IAvailableProjectsResponse } from 'Utils/typings/state/folder';
import { CreateFolderSuccessAction, DeleteFolderSuccessAction, SetFoldersAction, UpdateFolderSuccessAction } from './folderListActions';

export const createNewFolderThunk = (folderName: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders`;

  const payload = {
    name: folderName,
  };

  const response = await postJson<IFolder>(url, payload, token);

  // A failed response is returned, not thrown: it carries the server's reason (e.g. name already
  // taken) for the caller to show.
  if (response.success) {
    dispatch(CreateFolderSuccessAction(response.data));
  }

  return response;
};

export const getAllFoldersThunk = () => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  // Treasure Chest reads the organization it administers; a customer reads its own.
  const url = _ADMIN_ ? `${API_URL}/folders?orgId=${state.admin.orgId}` : `${API_URL}/folders`;
  const response = await getJson<IFolder[]>(url, token);

  if (!response.success) {
    throw new Error('Failed to fetch folders');
  } else {
    dispatch(SetFoldersAction(response.data));
  }
  dispatch(SetFoldersAction(response.data));

  return response.data;
};

export const updateFolderThunk = (folderId: string, folderName: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}`;

  const payload = {
    name: folderName,
  };

  const response = await patchJson<IFolder>(url, payload, token);

  // A failed response is returned, not thrown: it carries the server's reason (e.g. name already
  // taken) for the caller to show.
  if (response.success) {
    dispatch(UpdateFolderSuccessAction(response.data));
  }

  return response;
};

export const deleteFolderThunk = (folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}`;

  const response = await deleteRequest(url, token);

  if (!response.success) {
    throw new Error('Failed to delete folder');
  } else {
    dispatch(DeleteFolderSuccessAction(folderId));
  }

  return response;
};

export const moveProjectToFolderThunk = (projectId: string, folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/moveTo/${folderId}/${projectId}`;

  const response = await postJson(url, {}, token);

  if (!response.success) {
    throw new Error('Failed to move project to folder');
  } else {
    dispatch(getAllFoldersThunk() as any);
  }

  return response;
};

export const unassignProjectThunk = (projectId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/unassign/${projectId}`;

  const response = await postJson(url, {}, token);

  if (!response.success) {
    throw new Error('Failed to unassign project from folder');
  } else {
    dispatch(getAllFoldersThunk() as any);
  }

  return response;
};

// A folder's projects, answered rather than stored: the folder page stores them once it knows the answer is still for
// the folder on screen. A deleted folder, or another organization's, is refused (404) and throws like any failure.
export const getAllProjectsInFolderThunk = (folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}/projects`;

  const response = await getJson<IProject[]>(url, token);

  if (!response.success) {
    throw new Error(response.message || 'Failed to fetch projects in folder');
  }

  return response.data ?? [];
};

// This is used to move multiple projects to a folder
export const moveBulkProjectsToFolderThunk = (projectIds: string[], folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}/moveBulkProjects`;

  const response = await postJson(url, { projectIds }, token);

  if (!response.success) {
    throw new Error('Failed to move bulk projects to folder');
  }

  return response;
};

// This is used to get the projects that are not in the folder and are available to be added to the folder
export const getAvailableProjectsThunk = (folderId: string) => async (dispatch: Dispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/${folderId}/availableProjects`;

  const response = await getJson<IAvailableProjectsResponse>(url, token);

  if (!response.success) {
    throw new Error('Failed to fetch available projects');
  }

  return response.data;
};

// Saves the organization's folder order (every folder id, in display order) and replaces the list
// with the server's copy, which carries the saved positions. On failure the list is refetched so
// the screen never keeps an order the server refused.
export const reorderFoldersThunk = (folderIds: string[]) => async (dispatch: AppDispatch, getState: () => RootState) => {
  const state = getState();
  const { token } = state.auth;
  const url = `${API_URL}/folders/order`;

  const response = await patchJson<IFolder[]>(url, { folderIds }, token);

  if (response.success) {
    dispatch(SetFoldersAction(response.data));
  } else {
    try {
      await dispatch(getAllFoldersThunk());
    } catch (error) {
      // The optimistic order stays on screen; the next page load shows the saved order.
    }
  }

  return response;
};
